<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { CircleHelp } from 'lucide-vue-next'

const { t } = useI18n()

const props = defineProps({
  currentStep: {
    type: Number,
    required: true
  },
  totalSteps: {
    type: Number,
    required: true
  },
  stepTitle: {
    type: String,
    required: true
  },
  showBackButton: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['go-back', 'help-click'])

const progressPercentage = computed(() => {
  return (props.currentStep / props.totalSteps) * 100
})

const handleGoBack = () => {
  emit('go-back')
}

const handleHelpClick = () => {
  emit('help-click')
}
</script>

<template>
  <header>
    <div class="flex items-center justify-start min-h-12 relative sm:min-h-10">
      <!-- Кнопка назад -->
      <Transition name="back-button">
        <button
          v-if="showBackButton && currentStep > 1"
          @click="handleGoBack"
          class="icon-button absolute left-0 top-0 z-10"
          :aria-label="t('navigation.back')"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20v-2z"/>
          </svg>
        </button>
      </Transition>

      <!-- Название и номер шага -->
      <div
        class="flex-1 flex flex-row items-baseline justify-start gap-2 text-left pr-16 transition-padding duration-250 sm:pr-13"
        :class="{ 'pl-16 sm:pl-13': showBackButton && currentStep > 1 }"
      >
        <span class="text-lg font-semibold text-vault-text-primary whitespace-nowrap overflow-hidden text-ellipsis sm:text-base">{{ stepTitle }}</span>
        <span class="text-sm font-semibold text-vault-blue-400 whitespace-nowrap flex-shrink-0 sm:text-base">{{ currentStep }}/{{ totalSteps }}</span>
      </div>

      <!-- Кнопка помощи -->
      <button
        @click="handleHelpClick"
        class="icon-button absolute right-0 top-0"
        :aria-label="t('navigation.help')"
      >
        <CircleHelp :size="24" />
      </button>
    </div>

    <!-- Progress bar -->
    <div class="h-1 bg-blue-200/50 rounded-sm overflow-hidden relative">
      <div
        class="h-full bg-vault-blue-400 rounded-sm transition-all duration-300"
        :style="{ width: progressPercentage + '%' }"
      ></div>
      <!-- Step separators -->
      <div
        v-for="step in totalSteps - 1"
        :key="step"
        class="absolute top-0 w-1 h-full bg-white -translate-x-0.5 z-10"
        :style="{ left: (step / totalSteps * 100) + '%' }"
      ></div>
    </div>
  </header>
</template>

<style scoped>
/* Icon button with hover state */
.icon-button {
  @apply flex items-center justify-center w-12 h-12 border-none bg-transparent rounded-full text-vault-text-primary cursor-pointer transition-colors duration-150 overflow-hidden flex-shrink-0;
  @apply sm:w-10 sm:h-10;
}

.icon-button::before {
  content: '';
  @apply absolute inset-0 rounded-full bg-current opacity-0 transition-opacity duration-150;
}

.icon-button:hover::before {
  opacity: 0.08;
}

.icon-button:focus-visible::before {
  opacity: 0.12;
}

.icon-button:active::before {
  opacity: 0.16;
}

/* Transition for back button */
.back-button-enter-active,
.back-button-leave-active {
  transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
}

.back-button-enter-from,
.back-button-leave-to {
  opacity: 0;
  transform: scale(0.8);
}
</style>